import { useState, useEffect } from "react";
import axios from "axios";
import { Settings, Users, Gift, ArrowLeft, Pencil, X, Save, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ZODIAC_HOUSES = [
  "Aries", "Tauro", "Geminis", "Cancer", "Leo", "Virgo",
  "Libra", "Escorpio", "Sagitario", "Capricornio", "Acuario", "Piscis"
];

const EditUserModal = ({ user, onClose, onSave }) => {
  const [form, setForm] = useState({
    username: user.username || "",
    first_name: user.first_name || "",
    birthday: user.birthday || "",
    house: user.house || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/user/${user.user_id}`, form);
      toast.success(`Usuario actualizado`);
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="edit-user-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-white">Editar Usuario</h3>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-modal-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-slate-400 text-sm mb-4">ID: {user.user_id}</p>

        <div className="space-y-4">
          <div>
            <Label className="text-slate-300">Nombre</Label>
            <Input
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              className="bg-slate-800/50 border-slate-600 text-white"
              placeholder="Nombre"
              data-testid="edit-first-name"
            />
          </div>
          <div>
            <Label className="text-slate-300">Username (@)</Label>
            <Input
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="bg-slate-800/50 border-slate-600 text-white"
              placeholder="username (sin @)"
              data-testid="edit-username"
            />
          </div>
          <div>
            <Label className="text-slate-300">Cumple (DD/MM)</Label>
            <Input
              value={form.birthday}
              onChange={(e) => setForm({ ...form, birthday: e.target.value })}
              className="bg-slate-800/50 border-slate-600 text-white"
              placeholder="15/03"
              data-testid="edit-birthday"
            />
          </div>
          <div>
            <Label className="text-slate-300">Casa</Label>
            <select
              value={form.house}
              onChange={(e) => setForm({ ...form, house: e.target.value })}
              className="w-full p-2 rounded-md bg-slate-800/50 border border-slate-600 text-white"
              data-testid="edit-house"
            >
              <option value="">Sin casa</option>
              {ZODIAC_HOUSES.map((h) => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full mt-6 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          data-testid="save-user-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Guardando..." : "Guardar"}
        </Button>
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState("");
  const [points, setPoints] = useState("");
  const [pointType, setPointType] = useState("participation");
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const handleSendCofre = async () => {
    try {
      await axios.post(`${API}/test/send-cofre`);
      toast.success("Cofre enviado al grupo");
    } catch (error) {
      toast.error("Error al enviar cofre");
    }
  };

  const handleSendRuleta = async () => {
    try {
      await axios.post(`${API}/admin/send-ruleta`);
      toast.success("Ruleta enviada al grupo");
    } catch (error) {
      toast.error("Error al enviar ruleta");
    }
  };

  const handleSendBananaSplit = async () => {
    try {
      await axios.post(`${API}/admin/send-banana-split`);
      toast.success("Banana Split lanzado al grupo");
    } catch (error) {
      toast.error("Error al lanzar Banana Split");
    }
  };

  const handleSendTragaperras = async () => {
    try {
      await axios.post(`${API}/admin/send-tragaperras`);
      toast.success("Tragaperras lanzada al grupo");
    } catch (error) {
      toast.error("Error al lanzar Tragaperras");
    }
  };

  const handleAssignPoints = async (e) => {
    e.preventDefault();
    if (!selectedUser || !points) { toast.error("Completa todos los campos"); return; }
    try {
      await axios.post(`${API}/admin/assign-points`, {
        to_user_id: parseInt(selectedUser),
        points: parseInt(points),
        point_type: pointType
      });
      const label = pointType === "charisma" ? "carisma" : "participacion";
      toast.success(`${points} puntos de ${label} asignados`);
      setPoints(""); setSelectedUser(""); setPointType("participation"); fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al asignar puntos");
    }
  };

  const handleSetAdmin = async (userId, isAdmin) => {
    try {
      await axios.post(`${API}/admin/set-admin/${userId}?is_admin=${isAdmin}`);
      toast.success(`Usuario ${isAdmin ? 'promovido a' : 'removido de'} admin`);
      fetchUsers();
    } catch (error) {
      toast.error("Error al modificar admin");
    }
  };

  const getBadgeClass = (badge) => {
    const map = { "Lectora": "badge-lectora", "Activa": "badge-activa", "Influencer": "badge-influencer", "Leyenda Viva": "badge-leyenda", "Reina Vandala": "badge-reina" };
    return `badge ${map[badge] || 'badge-lectora'}`;
  };

  const filteredUsers = users.filter((u) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (u.first_name || "").toLowerCase().includes(term) ||
           (u.username || "").toLowerCase().includes(term) ||
           String(u.user_id).includes(term);
  });

  return (
    <div className="page-container">
      <div className="page-header">
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-4 text-purple-300 hover:text-purple-200" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver al Dashboard
        </Button>
        <h1 className="page-title" data-testid="admin-title">
          <Settings className="inline-block w-10 h-10 mb-1" /> Panel de Administracion
        </h1>
      </div>

      {editingUser && (
        <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} onSave={fetchUsers} />
      )}

      <div className="grid lg:grid-cols-2 gap-8">
            <Card className="glass-card border-slate-700" data-testid="assign-points-card">
              <CardHeader>
                <CardTitle className="ranking-title">
                  <Gift className="w-6 h-6 text-pink-400" /> Asignar Puntos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAssignPoints} className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Seleccionar Usuario</Label>
                    <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}
                      className="w-full p-2 rounded-md bg-slate-800/50 border border-slate-600 text-white" data-testid="user-select">
                      <option value="">Selecciona un usuario...</option>
                      {users.map((u) => (
                        <option key={u.user_id} value={u.user_id}>
                          {u.first_name || u.username || `Usuario ${u.user_id}`} - {u.current_month_points} pts
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Tipo de Puntos</Label>
                    <select value={pointType} onChange={(e) => setPointType(e.target.value)}
                      className="w-full p-2 rounded-md bg-slate-800/50 border border-slate-600 text-white" data-testid="point-type-select">
                      <option value="participation">Participacion (puntos del mes)</option>
                      <option value="charisma">Carisma (para repartir)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Cantidad de Puntos</Label>
                    <Input type="number" placeholder="Ej: 50" value={points} onChange={(e) => setPoints(e.target.value)}
                      className="bg-slate-800/50 border-slate-600 text-white" data-testid="points-input" />
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-purple-500 to-pink-500" data-testid="assign-points-button">
                    <Gift className="w-4 h-4 mr-2" /> Asignar Puntos
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="glass-card border-slate-700" data-testid="send-cofre-card">
              <CardHeader>
                <CardTitle className="ranking-title">
                  <Gift className="w-6 h-6 text-yellow-400" /> Acciones de Juego
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-slate-400 text-sm mb-2">Envia un cofre al grupo. La primera persona en abrirlo gana 5 puntos de carisma.</p>
                  <Button onClick={handleSendCofre} className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600" data-testid="send-cofre-btn">
                    <Gift className="w-4 h-4 mr-2" /> Enviar Cofre
                  </Button>
                </div>
                <div>
                  <p className="text-slate-400 text-sm mb-2">Envia una ruleta al grupo. La primera persona en girarla gana o pierde puntos al azar.</p>
                  <Button onClick={handleSendRuleta} className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600" data-testid="send-ruleta-btn">
                    🐔 Lanzar Huevo
                  </Button>
                </div>
                <div>
                  <p className="text-slate-400 text-sm mb-2">Bote de 50 puntos. Las primeras 5 personas que pulsen se lo reparten. Se cierra en 3 minutos.</p>
                  <Button onClick={handleSendBananaSplit} className="w-full bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600" data-testid="send-banana-split-btn">
                    🍌🍨 Banana Split
                  </Button>
                </div>
                <div>
                  <p className="text-slate-400 text-sm mb-2">Tragaperras: 3 iguales = JACKPOT +100 pts, 2 iguales = +10 pts, nada = -5 pts.</p>
                  <Button onClick={handleSendTragaperras} className="w-full bg-gradient-to-r from-red-500 to-purple-500 hover:from-red-600 hover:to-purple-600" data-testid="send-tragaperras-btn">
                    🎰 Tragaperras
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-slate-700" data-testid="users-list-card">
              <CardHeader>
                <CardTitle className="ranking-title">
                  <Users className="w-6 h-6 text-blue-400" /> Usuarios ({users.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-3 relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <Input placeholder="Buscar por nombre, username o ID..."
                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-slate-800/50 border-slate-600 text-white pl-9"
                    data-testid="search-users" />
                </div>
                <div className="space-y-3 max-h-[500px] overflow-y-auto" data-testid="users-list">
                  {loading ? (
                    <div className="text-center py-8 text-slate-400">Cargando...</div>
                  ) : filteredUsers.map((user) => (
                    <div key={user.user_id}
                      className="p-3 rounded-lg bg-slate-800/30 border border-slate-700 hover:border-purple-500/50 transition-all"
                      data-testid={`user-item-${user.user_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1">
                          <div className="font-semibold text-white">
                            {user.first_name || "Sin nombre"}
                            {user.username && <span className="text-slate-400 text-sm ml-2">@{user.username}</span>}
                            {!user.username && <span className="text-red-400 text-xs ml-2">(sin username)</span>}
                            {user.is_admin && <span className="ml-2 text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">ADMIN</span>}
                          </div>
                          <div className="text-xs text-slate-500">
                            ID: {user.user_id}
                            {user.house && <span className="ml-2">Casa: {user.house}</span>}
                            {user.birthday && <span className="ml-2">Cumple: {user.birthday}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={getBadgeClass(user.badge)}>{user.badge}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs text-slate-300 mb-2">
                        <div><span className="text-slate-500">Mes:</span> {user.current_month_points}</div>
                        <div><span className="text-slate-500">Total:</span> {user.historical_points}</div>
                        <div><span className="text-slate-500">Carisma:</span> {user.carisma_available}</div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline"
                          onClick={() => setEditingUser(user)}
                          className="flex-1 text-xs border-slate-600 text-slate-300 hover:bg-slate-700"
                          data-testid={`edit-user-${user.user_id}`}>
                          <Pencil className="w-3 h-3 mr-1" /> Editar
                        </Button>
                        <Button size="sm"
                          variant={user.is_admin ? "destructive" : "secondary"}
                          onClick={() => handleSetAdmin(user.user_id, !user.is_admin)}
                          className="flex-1 text-xs"
                          data-testid={`toggle-admin-${user.user_id}`}>
                          {user.is_admin ? 'Quitar Admin' : 'Hacer Admin'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
    </div>
  );
};

export default AdminPanel;
